'use client';

import { useState } from 'react';

import { Heading } from '@/hen-ui/atomics/Heading';
import { Text } from '@/hen-ui/atomics/Text';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { Flex } from '@/hen-ui/composites/Flex';
import { Split, SplitItem } from '@/hen-ui/composites/Split';
import {
  CareerExplorerPeek,
  type CareerExplorerPeekProps,
} from '@/hen-ui/composites/CareerExplorerPeek';
import {
  CareerExplorerRecommendations,
  type CareerExplorerRecommendationsProps,
} from '@/hen-ui/composites/CareerExplorerRecommendations';
import { shouldShowCareerExplorerRecommendations } from '@/hen-ui/composites/CareerExplorerRecommendations/visibility';
import {
  CareerExplorerSelector,
  type CareerExplorerSelection,
  type CareerExplorerSelectorOption,
} from '@/hen-ui/composites/CareerExplorerSelector';

export interface CareerExplorerProps {
  /** Optional experience heading shown above the selector. */
  heading?: string;
  /** Optional experience description shown under the heading. */
  description?: string;
  /** Careers listed in the dropdown. */
  careerOptions: CareerExplorerSelectorOption[];
  /** Single-select goal chips. */
  goalOptions: CareerExplorerSelectorOption[];
  /** Heading above the goal chips. */
  goalHeading?: string;
  /** Single-select experience-level chips. */
  levelOptions: CareerExplorerSelectorOption[];
  /** Heading above the experience-level chips. */
  levelHeading?: string;
  /** Question shown above the career dropdown. Omitted when empty. */
  careerQuestion?: string;
  /** Note shown beside the career question. Omitted when empty. */
  questionNote?: string;
  /**
   * Empty-state and clear option label for the career dropdown. A blank value
   * falls back to "Clear selection".
   */
  placeholder?: string;
  /**
   * Career the adapter accepted. When set, including `null`, the selector
   * follows this id instead of keeping a selection whose dataset did not load.
   */
  activeCareerId?: string | null;
  /** Mapped Peek props. Shown after the visitor selects a career. */
  peek?: CareerExplorerPeekProps;
  /** Mapped recommendation props. Omitted from the tree until a career is selected. */
  recommendations?: CareerExplorerRecommendationsProps;
  /**
   * Fires with the full selection. The Dynamic Experience adapter owns this
   * callback, so a saved DSL config does not serialize it.
   */
  onSelectionChange?: (selection: CareerExplorerSelection) => void;
}

/**
 * Assembles the career selector with the peek and recommendation panels.
 * The selector reports the career through `onSelectionChange`; this component
 * shows the peek after that career is set and both mapped panels are present.
 * The recommendations column is reserved only when that panel will render.
 * It does not load, sort, or persist a dataset.
 */
export const CareerExplorer = ({
  careerOptions,
  careerQuestion,
  description,
  questionNote,
  placeholder,
  activeCareerId,
  goalOptions,
  goalHeading,
  heading,
  levelOptions,
  levelHeading,
  onSelectionChange,
  peek,
  recommendations,
}: CareerExplorerProps) => {
  const [careerId, setCareerId] = useState<string | null>(null);

  const selectedCareerId =
    activeCareerId !== undefined ? activeCareerId : careerId;

  const showResults = Boolean(selectedCareerId && peek && recommendations);

  const showRecommendations =
    showResults &&
    recommendations !== undefined &&
    shouldShowCareerExplorerRecommendations(recommendations);

  const handleSelectionChange = (selection: CareerExplorerSelection) => {
    setCareerId(selection.careerId);

    onSelectionChange?.(selection);
  };

  return (
    <Flex
      direction='column'
      gap='lg'
      width='full'
      data-hen-id={COMPONENT_IDS.CareerExplorer}
    >
      {heading ? (
        <Heading variant='h4' tag='h2' color='secondary'>
          {heading}
        </Heading>
      ) : null}
      {description ? (
        <Text tag='p' variant='b3' color='secondary'>
          {description}
        </Text>
      ) : null}

      <Split
        gap='2xl'
        breakpoint='lg'
        ratio={showRecommendations ? '2/3-1/3' : 'equal'}
        className='lg:gap-xl'
      >
        <SplitItem>
          <Flex direction='column' gap='2xl' className='lg:gap-xl'>
            <CareerExplorerSelector
              heading={careerQuestion}
              questionNote={questionNote}
              placeholder={placeholder}
              careerOptions={careerOptions}
              goalOptions={goalOptions}
              goalHeading={goalHeading}
              levelOptions={levelOptions}
              levelHeading={levelHeading}
              controlledCareerId={
                activeCareerId !== undefined ? selectedCareerId : undefined
              }
              onSelectionChange={handleSelectionChange}
            />
            {showResults && peek ? <CareerExplorerPeek {...peek} /> : null}
          </Flex>
        </SplitItem>
        {showRecommendations && recommendations ? (
          <SplitItem>
            <CareerExplorerRecommendations {...recommendations} />
          </SplitItem>
        ) : null}
      </Split>
    </Flex>
  );
};
